import React, { useEffect, useMemo, useState } from 'react';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { InputNumber } from 'primereact/inputnumber';
import { useIntl } from 'react-intl';
import { useToast } from '../../contexts/ToastContext';
import { useConfirmationDialog } from '../../utils/ConfirmationDialogContext';
import { api } from '../../services/api-client';

type PaymentFrequency = 'monthly' | 'weekly' | 'per_session';

interface ISubscription {
  startDate?: string;
  endDate?: string;
  [key: string]: unknown;
}

interface ICoachPlan {
  id?: number;
  name?: string;
  price?: number;
  paymentFrequency?: PaymentFrequency;
  [key: string]: unknown;
}

interface IRegisterPaymentDialogProps {
  studentId: number;
  onClose: () => void;
  oldSubscription: ISubscription;
  oldCoachPlan: ICoachPlan;
}

const RegisterPaymentDialog = ({ studentId, onClose, oldSubscription, oldCoachPlan }: IRegisterPaymentDialogProps) => {
  const intl = useIntl();
  const { showToast } = useToast();
  const { showConfirmationDialog } = useConfirmationDialog();

  const planPrice = Number(oldCoachPlan?.price ?? 0);
  const frequency: PaymentFrequency | undefined = oldCoachPlan?.paymentFrequency;
  const isPerSession = frequency === 'per_session';

  const [paymentDate, setPaymentDate] = useState<Date | null>(new Date());
  const [amount, setAmount] = useState<number | null>(planPrice || null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setAmount(planPrice || null);
  }, [planPrice]);

  const periods = useMemo(() => {
    if (isPerSession || !planPrice || !amount) return 1;
    return Math.max(1, Math.floor(amount / planPrice));
  }, [amount, planPrice, isPerSession]);

  const previewEndDate = useMemo(() => {
    if (isPerSession || !paymentDate || !frequency) return null;
    const currentEnd = oldSubscription?.endDate ? new Date(oldSubscription.endDate as string) : null;
    const base = currentEnd && currentEnd.getTime() > paymentDate.getTime() ? currentEnd : paymentDate;
    const end = new Date(base);
    if (frequency === 'monthly') end.setMonth(end.getMonth() + periods);
    else if (frequency === 'weekly') end.setDate(end.getDate() + 7 * periods);
    return end;
  }, [frequency, isPerSession, paymentDate, periods, oldSubscription?.endDate]);

  const onClickRegisterPayment = () => {
    if (!paymentDate) {
      showToast(
        'error',
        intl.formatMessage({ id: 'common.error' }),
        intl.formatMessage({ id: 'payment.error.selectPaymentDate' })
      );
      return;
    }
    if (!amount || amount <= 0) {
      showToast(
        'error',
        intl.formatMessage({ id: 'common.error' }),
        intl.formatMessage({ id: 'payment.error.enterAmount' })
      );
      return;
    }

    const body = {
      clientId: studentId,
      paymentDate,
      amount
    };

    showConfirmationDialog({
      message: intl.formatMessage({ id: 'registerPayment.confirmation.message' }),
      header: intl.formatMessage({ id: 'common.confirmation' }),
      icon: 'pi pi-exclamation-triangle',
      accept: () => handleRegisterPayment(body)
    });
  };

  const handleRegisterPayment = async (body: Record<string, unknown>) => {
    try {
      setLoading(true);
      await api.subscription.registerPayment(body);
      showToast(
        'success',
        intl.formatMessage({ id: 'common.success' }),
        intl.formatMessage({ id: 'payment.success.paymentRegistered' })
      );
      onClose();
    } catch (error) {
      showToast('error', intl.formatMessage({ id: 'common.error' }), (error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    textTransform: 'uppercase',
    fontSize: '0.72rem',
    fontWeight: 600,
    letterSpacing: '0.03em',
    color: 'var(--ios-text-secondary)',
    marginBottom: '0.35rem'
  };

  return (
    <div className="p-3 flex flex-column gap-3">
      {oldCoachPlan?.name && (
        <div
          style={{
            padding: '0.6rem 0.75rem',
            borderRadius: 'var(--ios-radius-md)',
            background: 'var(--ios-surface-subtle)',
            fontSize: '0.85rem',
            color: 'var(--ios-text-secondary)'
          }}
        >
          <strong style={{ color: 'var(--ios-text)' }}>{oldCoachPlan.name}</strong>
          {planPrice > 0 && <> — ${planPrice.toLocaleString('es-AR')}</>}
          {frequency && (
            <>
              {' · '}
              {intl.formatMessage({
                id:
                  frequency === 'per_session'
                    ? 'coach.plan.paymentFrequency.perSession'
                    : `coach.plan.paymentFrequency.${frequency}`
              })}
            </>
          )}
        </div>
      )}

      <div className="p-field">
        <label htmlFor="paymentDate" style={labelStyle}>
          {intl.formatMessage({ id: 'payment.paymentDate' })}
        </label>
        <Calendar
          id="paymentDate"
          locale={intl.locale}
          dateFormat="dd/mm/yy"
          value={paymentDate}
          onChange={(e) => setPaymentDate(e.value as Date | null)}
          showIcon
          className="w-full"
        />
      </div>

      <div className="p-field">
        <label htmlFor="amount" style={labelStyle}>
          {intl.formatMessage({ id: 'payment.amount' })}
        </label>
        <InputNumber
          id="amount"
          value={amount}
          onValueChange={(e) => setAmount(e.value ?? null)}
          mode="currency"
          currency="ARS"
          locale="es-AR"
          className="w-full"
        />
      </div>

      {!isPerSession && previewEndDate && (
        <div
          style={{
            padding: '0.6rem 0.75rem',
            borderRadius: 'var(--ios-radius-md)',
            background: 'rgba(163, 230, 53, 0.18)',
            border: '1px solid rgba(10, 10, 10, 0.2)',
            fontSize: '0.85rem'
          }}
        >
          {intl.formatMessage(
            { id: 'registerPayment.preview' },
            {
              periods,
              endDate: previewEndDate.toLocaleDateString('es-AR')
            }
          )}
        </div>
      )}

      <div className="flex justify-content-end mt-2">
        <Button
          label={intl.formatMessage({ id: 'students.dialog.registerPayment' })}
          icon="pi pi-dollar"
          loading={loading}
          onClick={onClickRegisterPayment}
        />
      </div>
    </div>
  );
};

export default RegisterPaymentDialog;
